import React, { useState, useEffect } from 'react';
import { 
  FaTrash, FaEnvelope, FaTag, FaHeading, FaAlignLeft, FaCalendar, 
  FaCheckCircle, FaClock, FaCircle, FaSearch, FaFilter, FaCheckSquare, 
  FaRegSquare, FaCloudDownloadAlt, FaTimes, FaSort, FaSortUp, FaSortDown
} from 'react-icons/fa';
import { CSVLink } from 'react-csv';
import axios from 'axios';

const QueryManagement = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  
  // New States for Redesign
  const [showFilters, setShowFilters] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    fetchQueries();
  }, []);

  const fetchQueries = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setQueries(response.data.data);
      } else {
        throw new Error('Failed to fetch queries');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to fetch queries');
      console.error('Error fetching queries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingStatus(id);
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchQueries();
        setError('');
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this query?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        await fetchQueries();
        setError('');
        if (selectedIds.includes(id)) {
          setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
        }
      } else {
        throw new Error('Failed to delete query');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to delete query');
    }
  };

  const handleBatchStatusUpdate = async (newStatus) => {
    if (selectedIds.length === 0) return;
    const token = localStorage.getItem('adminToken');
    try {
      for (const id of selectedIds) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/${id}/status`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      await fetchQueries();
      setSelectedIds([]);
      setSelectionMode(false);
      setError('');
    } catch (error) {
      setError('Failed to complete batch status update');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} queries?`)) return;

    const token = localStorage.getItem('adminToken');
    try {
      for (const id of selectedIds) {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      await fetchQueries();
      setSelectedIds([]);
      setSelectionMode(false);
      setError('');
    } catch (error) {
      setError('Failed to complete batch delete');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'open': { color: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 border-yellow-100', icon: FaCircle },
      'in-progress': { color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border-blue-100', icon: FaClock },
      'resolved': { color: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border-green-100', icon: FaCheckCircle }
    };
    
    const config = statusConfig[status] || statusConfig['open'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${config.color}`}>
        <Icon className="w-2.5 h-2.5" />
        {status.replace('-', ' ')}
      </span>
    );
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredQueries = React.useMemo(() => {
    return queries
      .filter(query => {
        const queryStr = searchQuery.toLowerCase();
        const matchesSearch = !searchQuery || (
          query.email?.toLowerCase().includes(queryStr) ||
          query.category?.toLowerCase().includes(queryStr) ||
          query.subject?.toLowerCase().includes(queryStr) ||
          query.description?.toLowerCase().includes(queryStr)
        );
        const matchesStatus = filters.status === 'all' || query.status === filters.status;
        const matchesCategory = filters.category === 'all' || query.category === filters.category;
        
        const submissionDate = new Date(query.createdAt);
        const matchesStartDate = !filters.startDate || submissionDate >= new Date(filters.startDate);
        const matchesEndDate = !filters.endDate || submissionDate <= new Date(filters.endDate + 'T23:59:59');

        return matchesSearch && matchesStatus && matchesCategory && matchesStartDate && matchesEndDate;
      })
      .sort((a, b) => {
        if (!sortConfig.key) return 0;
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
  }, [queries, searchQuery, filters, sortConfig]);

  const categories = React.useMemo(() => {
    const cats = new Set(queries.map(q => q.category).filter(Boolean));
    return Array.from(cats);
  }, [queries]);

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredQueries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredQueries.map(r => r._id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const csvData = React.useMemo(() => {
    const dataToExport = selectedIds.length > 0 
      ? filteredQueries.filter(r => selectedIds.includes(r._id))
      : filteredQueries;
    
    return dataToExport.map(r => ({
      Email: r.email,
      Category: r.category,
      Subject: r.subject,
      Description: r.description,
      Status: r.status,
      SubmittedAt: formatDate(r.createdAt)
    }));
  }, [filteredQueries, selectedIds]);

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return <FaSort className="w-2.5 h-2.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="w-2.5 h-2.5 text-primary-500" /> : <FaSortDown className="w-2.5 h-2.5 text-primary-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-bold animate-pulse text-sm">Loading queries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 space-y-4">
      {/* Header and Stats */}
      <div className="px-4 lg:px-6 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Help Center Queries</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400 font-medium">Manage and respond to user queries from the help center</p>
          </div>
          <div className="flex items-center gap-3">
             <span className="inline-flex items-center px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-sm font-bold rounded-xl border border-primary-100 dark:border-primary-800">
              Total: {queries.length} {queries.length === 1 ? 'query' : 'queries'}
            </span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl animate-in fade-in duration-300">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'All Queries', value: queries.length, color: 'primary', icon: <FaSearch className="text-primary-600" /> },
            { label: 'Open', value: queries.filter(r => r.status === 'open').length, color: 'yellow', icon: <FaCircle className="text-yellow-600" /> },
            { label: 'In Progress', value: queries.filter(r => r.status === 'in-progress').length, color: 'blue', icon: <FaClock className="text-blue-600" /> },
            { label: 'Resolved', value: queries.filter(r => r.status === 'resolved').length, color: 'green', icon: <FaCheckCircle className="text-green-600" /> },
          ].map((stat, i) => (
            <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">{stat.label}</p>
                <div className={`p-2 rounded-lg bg-${stat.color}-50 dark:bg-${stat.color}-900/20`}>
                  {stat.icon}
                </div>
              </div>
              <p className="text-3xl font-black text-gray-900 dark:text-white">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Filter and Actions Bar */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-grow w-full">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by email, category, subject, description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap border ${
                  showFilters 
                  ? "bg-primary-50 text-primary-600 border-primary-200" 
                  : "bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-700 hover:bg-gray-50"
                }`}
              >
                <FaFilter className={showFilters ? "text-primary-600" : "text-gray-400"} />
                {showFilters ? "Hide Filters" : "Filters"}
              </button>

              <button
                onClick={() => setSelectionMode(!selectionMode)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${
                  selectionMode 
                  ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" 
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                }`}
              >
                <FaCheckSquare className={selectionMode ? "animate-pulse" : ""} />
                {selectionMode ? "Selection Active" : "Selection Mode"}
              </button>
              
              <CSVLink
                data={csvData}
                filename={`Queries_${new Date().toISOString().split('T')[0]}.csv`}
                className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap"
              >
                <FaCloudDownloadAlt size={18} />
                Export CSV {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
              </CSVLink>

              {selectionMode && selectedIds.length > 0 && (
                <div className="flex items-center gap-2">
                  <select
                    onChange={(e) => handleBatchStatusUpdate(e.target.value)}
                    className="px-4 py-2 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">Update Status...</option>
                    <option value="open">Mark Open</option>
                    <option value="in-progress">Mark In-Progress</option>
                    <option value="resolved">Mark Resolved</option>
                  </select>
                  <button
                    onClick={handleBatchDelete}
                    className="px-5 py-2.5 bg-red-600 text-white rounded-xl font-bold text-xs hover:bg-red-700 transition-all shadow-md shadow-red-500/30 whitespace-nowrap"
                  >
                    Delete ({selectedIds.length})
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">From Date</label>
                <div className="relative">
                  <FaCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                    className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl pl-9 pr-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">To Date</label>
                <div className="relative">
                  <FaCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                    className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl pl-9 pr-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Width Table Section */}
      <div className="bg-white dark:bg-dark-800 border-y border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead className="bg-gray-50/50 dark:bg-dark-700/50 border-b border-gray-100 dark:border-dark-700">
              <tr>
                {selectionMode && (
                  <th className="px-6 py-4 text-center w-12">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      {selectedIds.length === filteredQueries.length && filteredQueries.length > 0
                        ? <FaCheckSquare className="w-4 h-4" />
                        : <FaRegSquare className="w-4 h-4" />}
                    </button>
                  </th>
                )}
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('email')}>
                  <div className="flex items-center gap-1.5">
                    Email 
                    <SortIcon field="email" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('category')}>
                  <div className="flex items-center gap-1.5">
                    Category 
                    <SortIcon field="category" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Subject</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Description</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('status')}>
                  <div className="flex items-center gap-1.5">
                    Status 
                    <SortIcon field="status" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('createdAt')}>
                  <div className="flex items-center gap-1.5">
                    Submitted 
                    <SortIcon field="createdAt" />
                  </div>
                </th>
                <th className="px-6 py-4 text-right text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest sticky right-0 bg-gray-50 dark:bg-dark-700 shadow-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-800">
              {filteredQueries.length === 0 ? (
                <tr>
                  <td colSpan={selectionMode ? 8 : 7} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                       <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-full">
                        <FaSearch className="w-8 h-8 text-gray-300" />
                      </div>
                      <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                        {searchQuery ? 'No matches found.' : 'No queries found.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredQueries.map((query) => (
                  <tr 
                    key={query._id} 
                    className={`group transition-all duration-200 hover:bg-primary-50/20 dark:hover:bg-primary-900/10 cursor-pointer ${selectedIds.includes(query._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                    onClick={() => selectionMode ? toggleSelectOne(query._id) : setSelectedQuery(query)}
                  >
                    {selectionMode && (
                      <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => toggleSelectOne(query._id)}
                          className={`p-1.5 rounded-lg transition-colors ${selectedIds.includes(query._id) ? 'text-primary-600 bg-primary-100' : 'text-gray-400 hover:bg-gray-100'}`}
                        >
                          {selectedIds.includes(query._id) ? <FaCheckSquare className="w-4 h-4" /> : <FaRegSquare className="w-4 h-4" />}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <a href={`mailto:${query.email}`} className="text-primary-600 dark:text-primary-400 hover:underline text-xs font-bold" onClick={(e) => e.stopPropagation()}>
                        {query.email}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400">
                        {query.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-bold text-gray-900 dark:text-white line-clamp-1">{query.subject}</span>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                       <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1 font-medium italic">
                        "{query.description}"
                      </p>
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      {updatingStatus === query._id ? (
                        <div className="w-4 h-4 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                      ) : (
                        <select
                          value={query.status}
                          onChange={(e) => handleStatusUpdate(query._id, e.target.value)}
                          className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border transition-colors outline-none cursor-pointer ${
                            query.status === 'open' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                            query.status === 'in-progress' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                            'bg-green-50 text-green-700 border-green-100'
                          }`}
                        >
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-xs font-bold text-gray-600 dark:text-gray-400">
                        <FaCalendar className="w-3 h-3 text-gray-400" />
                        {formatDate(query.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-dark-800 group-hover:bg-primary-50/20 dark:group-hover:bg-primary-900/10 transition-colors shadow-left">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedQuery(query); }}
                          className="px-3 py-1.5 text-xs font-bold bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600"
                        >
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for detail view */}
      {selectedQuery && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedQuery(null)}>
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
             <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between bg-gray-50/50 dark:bg-dark-700/50">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Query Details</h2>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-widest font-black">Help Center Support</p>
              </div>
              <button
                onClick={() => setSelectedQuery(null)}
                className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-dark-600 rounded-xl transition-all shadow-sm"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                 <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaEnvelope className="w-2.5 h-2.5" /> Contact Email
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedQuery.email}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaTag className="w-2.5 h-2.5" /> Category
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedQuery.category}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700 sm:col-span-2">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaHeading className="w-2.5 h-2.5" /> Subject
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedQuery.subject}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    Status
                  </label>
                  <div className="mt-1">{getStatusBadge(selectedQuery.status)}</div>
                </div>
                 <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaCalendar className="w-2.5 h-2.5" /> Submitted On
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{formatDate(selectedQuery.createdAt)}</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-dark-700/50 p-5 rounded-2xl border border-gray-100 dark:border-dark-700">
                <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-widest">
                  <FaAlignLeft className="w-2.5 h-2.5" /> Detailed Description
                </label>
                <div className="p-4 bg-white dark:bg-dark-800 rounded-xl border border-gray-100 dark:border-dark-700 shadow-inner">
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap text-sm leading-relaxed font-medium">{selectedQuery.description}</p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3 bg-gray-50/50 dark:bg-dark-700/50">
              <button
                onClick={() => setSelectedQuery(null)}
                className="px-6 py-2.5 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-xs hover:bg-gray-50 border border-gray-200 dark:border-dark-700 transition-all shadow-sm"
              >
                Close
              </button>
              <button
                onClick={() => { handleDelete(selectedQuery._id); setSelectedQuery(null); }}
                className="px-6 py-2.5 bg-red-600 text-white rounded-xl font-bold text-xs hover:bg-red-700 transition-all shadow-md shadow-red-500/30"
              >
                Delete Query
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryManagement;
